import { composeSelector } from "seneca-common/utils/selectors/compose-selectors";

import {
  assignmentQuizDetailsNeedsFetching,
  getStudentAssignmentsState
} from "./";
import { getMeta } from "./state";
import * as meta from "./state/meta";

type State = any;

function getMetaState(state: State) {
  return getMeta(getStudentAssignmentsState(state));
}

function getFutureAssignmentsState(state: any) {
  return getMetaState(state).futureAssignments;
}

function getPastAssignmentsState(state: any) {
  return getMetaState(state).pastAssignments;
}

function getChildAssignmentsState(state: any) {
  return getMetaState(state).childAssignments;
}

function getChildAssignmentState(state: any) {
  return getMetaState(state).childAssignment;
}

// single assignment

export const singleChildAssignmentIsFetching: (
  state: State,
  assignmentId: string
) => boolean = composeSelector(
  // @ts-ignore
  getChildAssignmentState,
  meta.singleAssignmentIsFetching
);

export const singleChildAssignmentFetchSucceeded: (
  state: State,
  assignmentId: string
) => boolean = composeSelector(
  // @ts-ignore
  getChildAssignmentState,
  meta.singleAssignmentFetchSucceeded
);

export const singleChildAssignmentFetchErrored: (
  state: State,
  assignmentId: string
) => boolean = composeSelector(
  // @ts-ignore
  getChildAssignmentState,
  meta.singleAssignmentFetchErrored
);

export const singleChildAssignmentFetchFinished: (
  state: State,
  assignmentId: string
) => boolean = composeSelector(
  // @ts-ignore
  getChildAssignmentState,
  meta.singleAssignmentFetchFinished
);

export const singleChildAssignmentNeedsFetching: (
  state: State,
  assignmentId: string
) => boolean = composeSelector(
  // @ts-ignore
  getChildAssignmentState,
  meta.singleAssignmentNeedsFetching
);

// all assignments

export const childAssignmentsAreFetching: (
  state: State,
  userId: string
) => boolean = composeSelector(
  // @ts-ignore
  getChildAssignmentsState,
  meta.childAssignmentsAreFetching
);

export const childAssignmentsFetchSucceeded: (
  state: State,
  userId: string
) => boolean = composeSelector(
  // @ts-ignore
  getChildAssignmentsState,
  meta.childAssignmentsFetchSucceeded
);

export const childAssignmentsFetchErrored: (
  state: State,
  userId: string
) => boolean = composeSelector(
  // @ts-ignore
  getChildAssignmentsState,
  meta.childAssignmentsFetchCreateErrored
);

export const childAssignmentsFetchFinished: (
  state: State,
  userId: string
) => boolean = composeSelector(
  // @ts-ignore
  getChildAssignmentsState,
  meta.childAssignmentsFetchFinished
);

export const childAssignmentsNeedFetching: (
  state: State,
  userId: string
) => boolean = composeSelector(
  // @ts-ignore
  getChildAssignmentsState,
  meta.childAssignmentsNeedFetching
);

// single or all assignments meta states
// optimization to avoid refetching single assignment if all assignments have fetched
// assumes single & all assignments fetch identical data

export const childAssignmentIsFetching: (
  state: State,
  userId: string,
  assignmentId: string
) => boolean = (state, userId, assignmentId) =>
  singleChildAssignmentIsFetching(state, assignmentId) ||
  childAssignmentsAreFetching(state, userId);

export const childAssignmentFetchSucceeded: (
  state: State,
  userId: string,
  assignmentId: string
) => boolean = (state, userId, assignmentId) =>
  singleChildAssignmentFetchSucceeded(state, assignmentId) ||
  childAssignmentsFetchSucceeded(state, userId);

export const childAssignmentFetchErrored: (
  state: State,
  userId: string,
  assignmentId: string
) => boolean = (state, userId, assignmentId) =>
  (singleChildAssignmentFetchFinished(state, assignmentId) &&
    singleChildAssignmentFetchErrored(state, assignmentId)) ||
  (childAssignmentsFetchFinished(state, userId) &&
    childAssignmentsFetchErrored(state, userId));

export const childAssignmentFetchFinished: (
  state: State,
  userId: string,
  assignmentId: string
) => boolean = (state, userId, assignmentId) =>
  singleChildAssignmentFetchFinished(state, assignmentId) ||
  childAssignmentsFetchFinished(state, userId);

export const childAssignmentNeedsFetching: (
  state: State,
  userId: string,
  assignmentId: string
) => boolean = (state, userId, assignmentId) =>
  assignmentQuizDetailsNeedsFetching(state, assignmentId) ||
  (singleChildAssignmentNeedsFetching(state, assignmentId) &&
    childAssignmentsNeedFetching(state, userId));

// fetch assignments from [-3days, future)
export const currentStudentAssignmentsAreFetching: (state: State) => boolean =
  composeSelector(
    // @ts-ignore
    getFutureAssignmentsState,
    meta.allStudentAssignmentsAreFetching
  );

export const currentStudentAssignmentsFetchSucceeded: (
  state: State
) => boolean = composeSelector(
  // @ts-ignore
  getFutureAssignmentsState,
  meta.allStudentAssignmentsFetchSucceeded
);

export const currentStudentAssignmentsFetchErrored: (state: State) => boolean =
  composeSelector(
    // @ts-ignore
    getFutureAssignmentsState,
    meta.allStudentAssignmentsFetchCreateErrored
  );

export const currentStudentAssignmentsFetchFinished: (state: State) => boolean =
  composeSelector(
    // @ts-ignore
    getFutureAssignmentsState,
    meta.allStudentAssignmentsFetchFinished
  );

export const currentStudentAssignmentsNeedFetching: (state: State) => boolean =
  composeSelector(
    // @ts-ignore
    getFutureAssignmentsState,
    meta.allStudentAssignmentsNeedFetching
  );

//fetch all assignments
export const allStudentAssignmentsAreFetching: (state: State) => boolean =
  composeSelector(
    // @ts-ignore
    getPastAssignmentsState,
    meta.allStudentAssignmentsAreFetching
  );

export const allStudentAssignmentsFetchSucceeded: (state: State) => boolean =
  composeSelector(
    // @ts-ignore
    getPastAssignmentsState,
    meta.allStudentAssignmentsFetchSucceeded
  );

export const allStudentAssignmentsFetchErrored: (state: State) => boolean =
  composeSelector(
    // @ts-ignore
    getPastAssignmentsState,
    meta.allStudentAssignmentsFetchCreateErrored
  );

export const allStudentAssignmentsFetchFinished: (state: State) => boolean =
  composeSelector(
    // @ts-ignore
    getPastAssignmentsState,
    meta.allStudentAssignmentsFetchFinished
  );

export const allStudentAssignmentsNeedFetching: (state: State) => boolean =
  composeSelector(
    // @ts-ignore
    getPastAssignmentsState,
    meta.allStudentAssignmentsNeedFetching
  );

// fetch one assignment
export const studentAssignmentIsFetching: (
  state: State,
  assignmentId: string
) => boolean = composeSelector(
  // @ts-ignore
  getPastAssignmentsState,
  meta.singleAssignmentIsFetching
);

export const studentAssignmentFetchSucceeded: (
  state: State,
  assignmentId: string
) => boolean = composeSelector(
  // @ts-ignore
  getPastAssignmentsState,
  meta.singleAssignmentFetchSucceeded
);

export const studentAssignmentFetchErrored: (
  state: State,
  assignmentId: string
) => boolean = composeSelector(
  // @ts-ignore
  getPastAssignmentsState,
  meta.singleAssignmentFetchErrored
);

export const studentAssignmentFetchFinished: (
  state: State,
  assignmentId: string
) => boolean = composeSelector(
  // @ts-ignore
  getPastAssignmentsState,
  meta.singleAssignmentFetchFinished
);

export const studentAssignmentNeedsFetching: (
  state: State,
  assignmentId: string
) => boolean = composeSelector(
  // @ts-ignore
  getPastAssignmentsState,
  meta.singleAssignmentNeedsFetching
);
